import {
	DropdownMenu,
	DropdownMenuContent,
	DropdownMenuItem,
	DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { FC, useEffect } from "react";
import { Button } from "../ui/button";
import { ChevronDown } from "lucide-react";

type PAGE_OPTION = {
	value: number;
	label: string;
};

const PAGES_OPTIONS: PAGE_OPTION[] = [
	{ value: 5, label: "5" },
	{ value: 10, label: "10" },
	{ value: 20, label: "20" },
	{ value: 50, label: "50" },
	{ value: 100, label: "100" },
];

interface PerPageSelectionProps {
	perPage: number;
	setPerPage: (perPage: number) => void;
	total: number;
	loading?: boolean;
}

export const PerPageSelection: FC<PerPageSelectionProps> = ({ perPage, setPerPage, total, loading = false }) => {
	useEffect(() => {
		if (total === 0) return;

		const currentIndex = PAGES_OPTIONS.findIndex(opt => opt.value === perPage);
		const prevOption = currentIndex > 0 ? PAGES_OPTIONS[currentIndex - 1] : null;

		if (perPage > total && (!prevOption || total <= prevOption.value)) {
			const smallestValid = [...PAGES_OPTIONS].reverse().find(opt => opt.value <= total);
			if (smallestValid) setPerPage(smallestValid.value);
		}
	}, [perPage, total, setPerPage]);

	const displayedPerPage = perPage;

	const isOptionDisabled = (index: number, value: number) => {
		if (total === 0) return false;
		if (index === 0) return value > total;
		const prevValue = PAGES_OPTIONS[index - 1].value;
		return total <= prevValue;
	};

	return (
		<DropdownMenu>
			<DropdownMenuTrigger asChild>
				<Button variant="outline" size="sm" className="px-3 gap-1" disabled={loading}>
					{displayedPerPage}
					<ChevronDown className="w-4 h-4 opacity-70" />
				</Button>
			</DropdownMenuTrigger>
			<DropdownMenuContent align="end" className="min-w-[3rem] px-2">
				{PAGES_OPTIONS.map(({ value, label }, index) => {
					const disabled = isOptionDisabled(index, value);
					return (
						<DropdownMenuItem
							key={value}
							onClick={() => !disabled && setPerPage(value)}
							disabled={disabled}
							className={`flex items-center gap-2 justify-center text-sm ${
								displayedPerPage === value ? "bg-gray-100 font-semibold" : ""
							}`}
						>
							<span className={disabled ? "opacity-40" : ""}>{label}</span>
						</DropdownMenuItem>
					);
				})}
			</DropdownMenuContent>
		</DropdownMenu>
	);
};
